// Modal shown when an auto-join via URL link fails (the target lobby is
// no longer available — owner left, all members migrated to a different
// region whose code rotated, etc.). Single button: "Start your own party"
// dismisses the modal so the user can keep playing in their own (now
// freshly-created) solo party.
//
// Visually a SearchingModal-styled card with a lock-icon-flavoured
// title and one CTA. Mounts at z-index 200 above the PartyPanel.

import { menuAudio } from '../MenuAudio';
import { t } from '../../i18n';

const STYLES_ID = 'lobby-unavailable-modal-styles';
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

.lobby-unavailable-modal {
  position: fixed; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(8px);
  z-index: 200;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
  color: #FFFFFF;
  opacity: 0;
  transition: opacity 200ms ease;
  pointer-events: auto;
}
.lobby-unavailable-modal.shown { opacity: 1; }
.lobby-unavailable-modal.closing { opacity: 0; }
.lobby-unavailable-modal .card {
  pointer-events: auto;
  background: #1E232C;
  border-radius: 18px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.5);
  padding: 32px 36px;
  display: flex; flex-direction: column; align-items: center;
  gap: 18px;
  min-width: 460px; max-width: 90vw;
  transform: scale(0.96);
  transition: transform 200ms cubic-bezier(0.2, 0.9, 0.2, 1);
}
.lobby-unavailable-modal.shown .card { transform: scale(1); }
.lobby-unavailable-modal .title {
  font-size: 30px;
  -webkit-text-stroke: 3px rgba(0,0,0,0.22);
  paint-order: stroke fill;
}
.lobby-unavailable-modal .body {
  font-size: 20px;
  color: rgba(255,255,255,0.85);
  text-align: center;
  max-width: 380px;
  line-height: 1.3;
}
.lobby-unavailable-modal .actions {
  display: flex; gap: 14px;
  margin-top: 6px;
}
.lobby-unavailable-modal .btn {
  border: 0; cursor: pointer;
  font-family: 'Chewy', sans-serif;
  font-size: 22px;
  padding: 12px 26px;
  border-radius: 14px;
  color: #FFFFFF;
  -webkit-text-stroke: 3px #356B8C;
  paint-order: stroke fill;
  background: linear-gradient(180deg, #7DD2F5 0%, #3DA6FF 100%);
  box-shadow: 0 4px 0 #256AA0, 0 8px 18px rgba(0,0,0,0.3);
  transition: transform 120ms ease, filter 120ms ease;
}
.lobby-unavailable-modal .btn:hover,
.lobby-unavailable-modal .btn.focused {
  filter: brightness(1.04);
  outline: 4px solid rgba(255,255,255,0.6);
  outline-offset: 3px;
}
.lobby-unavailable-modal .btn:active { transform: translateY(3px); }
`;

export interface LobbyUnavailableModalHandle {
  dismiss(): void;
}

export function showLobbyUnavailableModal(onDismiss: () => void): LobbyUnavailableModalHandle {
  ensureStyles();
  const root = ensureRoot();
  const wrap = document.createElement('div');
  wrap.className = 'lobby-unavailable-modal';
  wrap.innerHTML = `
    <div class="card">
      <div class="title">${t('lobbyUnavailable.title')}</div>
      <div class="body">${t('lobbyUnavailable.body')}</div>
      <div class="actions">
        <button type="button" class="btn">${t('lobbyUnavailable.startOwn')}</button>
      </div>
    </div>
  `;
  root.appendChild(wrap);
  requestAnimationFrame(() => wrap.classList.add('shown'));

  let dismissed = false;
  const btn = wrap.querySelector('.btn') as HTMLButtonElement;

  const finish = (): void => {
    if (dismissed) return;
    dismissed = true;
    removeEventListener('keydown', onKeyDown, { capture: true } as EventListenerOptions);
    wrap.classList.add('closing');
    setTimeout(() => wrap.remove(), 220);
    onDismiss();
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (dismissed) return;
    if (e.code === 'Escape' || e.code === 'Backspace' || e.code === 'Enter') {
      e.preventDefault(); e.stopPropagation();
      menuAudio.playClick();
      finish();
    }
  };
  btn.addEventListener('click', () => { menuAudio.playClick(); finish(); });
  addEventListener('keydown', onKeyDown, { capture: true });

  // Default focus on the button so Enter/Space activates immediately.
  setTimeout(() => btn.focus(), 0);

  return { dismiss(): void { finish(); } };
}

function ensureRoot(): HTMLElement {
  let root = document.getElementById('match-modals');
  if (!root) {
    root = document.createElement('div');
    root.id = 'match-modals';
    root.style.cssText = `position:fixed; inset:0; z-index:150; pointer-events:none;`;
    document.body.appendChild(root);
  }
  return root;
}

function ensureStyles(): void {
  if (document.getElementById(STYLES_ID)) return;
  const s = document.createElement('style');
  s.id = STYLES_ID;
  s.textContent = STYLES;
  document.head.appendChild(s);
}
